import React from 'react';
import {StyleSheet, View, useWindowDimensions} from 'react-native';
import CCText from '@/Atoms/CCText';
import {
  WIDE_BREAKPOINT,
  MEDAL,
  avatarColorForUser,
  lbColors,
  lbRadius,
} from '../leaderboardTheme';
import {initialsForName} from '../useLeaderboardData';
import {useWinnersData} from '../useWinnersData';
import {WinnerVM, WinnerPeriodVM} from '../types';
import PlayerAvatar from './PlayerAvatar';

// Icon shown on each winner's prize chip — derived from source + period type
// rather than a specific prize title, so a new prize slotted into the same
// kind (e.g. a different weekly-draw item) picks up the right icon for free.
const PRIZE_ICON: Record<string, string> = {
  'leaderboard:weekly': '👕',
  'draw:weekly': '🎟️',
  'draw:monthly': '🏆',
};

/**
 * Winners section — one block per closed period (weekly/monthly), each with
 * the leaderboard Top-3 and/or lucky-draw winner(s) for that period; an
 * empty state before the first period closes; "you won" highlight on the
 * signed-in player's own row.
 * Design source: scratchpad/engagement_canvas.dc.html (#cc-winners,
 * `winnerPeriods`/`winnersEmpty` scenarios).
 *
 * `enabled` (default true) is Prizes v2's `winners_available` gate, passed
 * down from the engagement summary by the screen — when false, neither the
 * winners endpoint nor any of this section's UI mounts (see useWinnersData).
 */
export default function WinnersSection({
  enabled = true,
}: {
  enabled?: boolean;
}) {
  const {width} = useWindowDimensions();
  const wide = width >= WIDE_BREAKPOINT;
  const {winners} = useWinnersData({enabled});
  const periods = winners.periods;

  if (!enabled) {
    return null;
  }

  return (
    <View style={styles.section}>
      <View style={styles.header}>
        <CCText fontSize={19}>🏆</CCText>
        <CCText fontSize={17} fontWeight="bold" color={lbColors.ink}>
          Winners
        </CCText>
        <CCText fontSize={10.5} fontWeight="semibold" color={lbColors.faint}>
          new winners every Monday morning
        </CCText>
      </View>
      {periods.length === 0 ? (
        <EmptyState />
      ) : (
        periods.map(p => (
          <PeriodBlock
            key={`${p.periodType}-${p.periodStart}`}
            period={p}
            wide={wide}
          />
        ))
      )}
    </View>
  );
}

function EmptyState() {
  return (
    <View style={styles.empty}>
      <CCText fontSize={26}>🎉</CCText>
      <CCText fontSize={14} fontWeight="bold" color={lbColors.inkSoft}>
        First winners announced Monday!
      </CCText>
      <CCText fontSize={11} fontWeight="semibold" color={lbColors.faint}>
        Keep your streak alive and climb the board to be in the running.
      </CCText>
    </View>
  );
}

function PeriodBlock({period, wide}: {period: WinnerPeriodVM; wide: boolean}) {
  const typeChipBg =
    period.periodType === 'monthly'
      ? 'rgba(245,195,60,0.25)'
      : lbColors.goldSoft;
  return (
    <View style={styles.periodBlock}>
      <View style={styles.periodHead}>
        <View style={[styles.typeChip, {backgroundColor: typeChipBg}]}>
          <CCText fontSize={8.5} fontWeight="bold" color={lbColors.youChipSub}>
            {period.periodType.toUpperCase()}
          </CCText>
        </View>
        <CCText fontSize={13} fontWeight="bold" color={lbColors.ink}>
          {period.label}
        </CCText>
      </View>
      {period.winners.map(w => (
        <WinnerRow
          key={`${w.source}-${w.userid}-${w.rank ?? 'draw'}`}
          winner={w}
          periodType={period.periodType}
          wide={wide}
        />
      ))}
    </View>
  );
}

function WinnerRow({
  winner,
  periodType,
  wide,
}: {
  winner: WinnerVM;
  periodType: WinnerPeriodVM['periodType'];
  wide: boolean;
}) {
  const medal = winner.rank != null ? MEDAL[winner.rank] : null;
  // The canvas shows "N-day streak" for draw winners, but the backend never
  // records a metric_value (e.g. streak_count) for source='draw' winners
  // today — render a static "Lucky draw" label instead of fabricating a
  // number until that's added (deliberate deviation from canvas).
  const metricLabel =
    winner.source === 'leaderboard' && winner.metricValue != null
      ? `${winner.metricValue} wins`
      : 'Lucky draw';
  const prizeIcon = PRIZE_ICON[`${winner.source}:${periodType}`] || '🎁';

  return (
    <View
      style={[
        styles.row,
        {backgroundColor: winner.you ? lbColors.youRowBg : lbColors.rowBg},
      ]}>
      <View
        style={[
          styles.badge,
          {backgroundColor: medal ? medal.bg : lbColors.goldSoft},
        ]}>
        <CCText
          fontSize={12}
          fontWeight="bold"
          color={medal ? medal.tx : lbColors.youChipSub}>
          {winner.rank != null ? winner.rank : '🎟'}
        </CCText>
      </View>
      <PlayerAvatar
        initials={initialsForName(winner.name)}
        color={winner.you ? lbColors.teal : avatarColorForUser(winner.userid)}
        avatarUrl={winner.avatarUrl}
        size={32}
      />
      <View style={styles.nameCell}>
        <CCText
          fontSize={13.5}
          fontWeight="bold"
          color={lbColors.ink}
          showEllipses
          style={styles.name}>
          {winner.name}
        </CCText>
        {winner.you ? (
          <View style={styles.youTag}>
            <CCText fontSize={9} fontWeight="bold" color="#FFFFFF">
              YOU
            </CCText>
          </View>
        ) : null}
      </View>
      <View style={styles.prizeChip}>
        <CCText fontSize={10.5} fontWeight="bold" color={lbColors.youChipSub}>
          {prizeIcon} {winner.prize.short || winner.prize.title}
        </CCText>
      </View>
      {/* Metric column ("42 wins" / "9-day streak") — wide only, dropped on
          narrow rows to keep the name + prize chip legible (matches the
          canvas's compact mobile winner rows). */}
      {wide ? (
        <CCText
          fontSize={11}
          fontWeight="bold"
          color={lbColors.muted}
          style={styles.metric}>
          {metricLabel}
        </CCText>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  section: {
    backgroundColor: lbColors.card,
    borderRadius: lbRadius.xl,
    padding: 20,
    marginTop: 16,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginBottom: 12,
    flexWrap: 'wrap',
  },
  empty: {
    borderWidth: 2,
    borderStyle: 'dashed',
    borderColor: lbColors.line,
    borderRadius: lbRadius.lg,
    padding: 26,
    alignItems: 'center',
    gap: 6,
  },
  periodBlock: {marginBottom: 13},
  periodHead: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 7,
  },
  typeChip: {paddingHorizontal: 9, paddingVertical: 3, borderRadius: 9},
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 11,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: lbRadius.md,
    marginBottom: 4,
  },
  badge: {
    minWidth: 27,
    height: 27,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
  },
  nameCell: {
    flex: 1,
    minWidth: 0,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 7,
  },
  name: {flexShrink: 1},
  youTag: {
    backgroundColor: lbColors.teal,
    paddingHorizontal: 7,
    paddingVertical: 1,
    borderRadius: lbRadius.pill,
  },
  prizeChip: {
    backgroundColor: lbColors.winnerPrizeChipBg,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 11,
  },
  metric: {width: 80, textAlign: 'right'},
});
